import type {
  ExpenseDataSchema,
  FrequencyType,
  IncomeDataSchema,
} from "@/data";
import { match } from "ts-pattern";

interface MortgageData {
  readonly loanAmount: number;
  readonly interestRate: number;
  readonly loanTerm: number;
  readonly repaymentFrequency: FrequencyType;
  readonly interestOnly: boolean;
}

// https://mozo.com.au/home-loans/resources/guides/how-are-mortgage-repayments-calculated
// Function: M = P [r(1+r)^n] / [(1+r)^n – 1
const calculateMortgageCostPerYear = (data: MortgageData): number => {
  const { loanAmount, loanTerm, interestRate, repaymentFrequency, interestOnly } = data;

  const frequencyMultipler = match(repaymentFrequency)
      .with("Weekly", () => 52.0)
      .with("Fortnightly", () => 26.0)
      .with("Monthly", () => 12.0)
      .with("Quarterly", () => 4.0)
      .with("Yearly", () => 1.0)
      .exhaustive();

  const P = loanAmount;
  const r = interestRate / frequencyMultipler;
  const n = loanTerm * frequencyMultipler;
  const numerator = r * Math.pow(1 + r, n);
  const divisor = Math.pow(1 + r, n) - 1;
  const piRepaymentPerFreq = P * (numerator / divisor);
  const piRepaymentPerYear = piRepaymentPerFreq * frequencyMultipler;

  const iRepaymentPerYear = (loanAmount * interestRate);
  return interestOnly ? iRepaymentPerYear : piRepaymentPerYear;
};

const calculateTotalExpensesPerYear = (data: ExpenseDataSchema[]) => {
  const [essential, nonEssential] = data.reduce(
    (acc, d) => {
      const idx = d.essential ? 0 : 1;
      if (d.selected) {
        acc[idx].push(d);
      }
      return acc;
    },
    [[], []] as [ExpenseDataSchema[], ExpenseDataSchema[]]
  );

  const frequencyToMultipler = (frequency: FrequencyType) =>
    match(frequency)
      .with("Weekly", () => 52)
      .with("Fortnightly", () => 26)
      .with("Monthly", () => 12)
      .with("Quarterly", () => 4)
      .with("Yearly", () => 1)
      .exhaustive();

  const essentialTotal = essential
    .map((data) => data.amount * frequencyToMultipler(data.frequency))
    .reduce((prev, current) => prev + current, 0);
  const nonEssentialTotal = nonEssential
    .map((data) => data.amount * frequencyToMultipler(data.frequency))
    .reduce((prev, current) => prev + current, 0);

  return {
    essential: essentialTotal,
    nonEssential: nonEssentialTotal,
    total: essentialTotal + nonEssentialTotal,
  };
};

const calculateTotalIncomePerYear = (data: IncomeDataSchema[]) => {
  const frequencyToMultipler = (frequency: FrequencyType) =>
    match(frequency)
      .with("Weekly", () => 52)
      .with("Fortnightly", () => 26)
      .with("Monthly", () => 12)
      .with("Quarterly", () => 4)
      .with("Yearly", () => 1)
      .exhaustive();

  return data
    .map((val) =>
      val.selected ? val.amount * frequencyToMultipler(val.frequency) : 0
    )
    .reduce((prev, current) => prev + current, 0);
};

export {
  calculateMortgageCostPerYear,
  calculateTotalExpensesPerYear,
  calculateTotalIncomePerYear,
};
